import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";
import { keyframes } from "@mui/system";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardIcon from "@mui/icons-material/Dashboard";
import MapIcon from "@mui/icons-material/Map";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { useLocation, useNavigate } from "react-router-dom";
import packageJson from "../../../package.json";

type MenuItem = {
  key: string;
  label: string;
  path: string;
  icon: React.ReactNode;
};

const menuItems: MenuItem[] = [
  {
    key: "dashboard",
    label: "Dashboard",
    path: "/QueueBoard",
    icon: <DashboardIcon />,
  },
  {
    key: "intakeCalendar",
    label: "Intake Calendar",
    path: "/IntakeCalendar",
    icon: <CalendarMonthIcon />,
  },
  {
    key: "technicianCalendar",
    label: "Service Calendar V2",
    path: "/TechnicianCalendar",
    icon: <CalendarMonthIcon />,
  },
  {
    key: "logistics",
    label: "Logistics",
    path: "/Logistics",
    icon: <MapIcon />,
  },
  
  
];

const COLLAPSED_WIDTH = 72;
const EXPANDED_WIDTH = 240;

const SIDEBAR_COLLAPSE_KEY = (username: string | null) =>
  `sidebar_collapsed_${username ?? "anonymous"}`;
const AUTO_HIDE_DELAY = 3 * 60 * 1000;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Sidebar: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [tipOpen, setTipOpen] = useState<boolean>(false);
  const tipTimer = useRef<number | null>(null);
  const autoHideTimer = useRef<number | null>(null);

  const clearAutoHideTimer = useCallback(() => {
    if (autoHideTimer.current) {
      window.clearTimeout(autoHideTimer.current);
      autoHideTimer.current = null;
    }
  }, []);

  const scheduleAutoHide = useCallback(() => {
    clearAutoHideTimer();
    autoHideTimer.current = window.setTimeout(() => {
      setCollapsed(true);
    }, AUTO_HIDE_DELAY);
  }, [clearAutoHideTimer]);

  useEffect(() => {
    const username = localStorage.getItem("username");
    const saved = localStorage.getItem(SIDEBAR_COLLAPSE_KEY(username));
    if (saved === "true" || saved === "false") setCollapsed(saved === "true");
  }, []);

  useEffect(() => {
    const username = localStorage.getItem("username");
    localStorage.setItem(SIDEBAR_COLLAPSE_KEY(username), String(collapsed));
  }, [collapsed]);

  useEffect(() => {
    if (!collapsed) {
      scheduleAutoHide();
    } else {
      clearAutoHideTimer();
    }

    return clearAutoHideTimer;
  }, [collapsed, scheduleAutoHide, clearAutoHideTimer]);

  const width = collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  const getItemStyles = (active: boolean) => ({
    borderRadius: 2,
    mb: 0.75,
    mx: collapsed ? 0.5 : 0,
    px: collapsed ? 1.5 : 2,
    py: 1.25,
    minHeight: 48,
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative" as const,
    overflow: "hidden",
    justifyContent: collapsed ? "center" : "flex-start",
    "& .MuiListItemIcon-root": {
      minWidth: collapsed ? 0 : 40,
      mr: collapsed ? 0 : 1.5,
      color: active
        ? theme.palette.primary.contrastText
        : alpha(theme.palette.text.primary, 0.7),
      transition: "all 0.2s ease",
      "& svg": {
        fontSize: "1.35rem",
      },
    },
    "& .MuiListItemText-primary": {
      fontWeight: active ? 600 : 500,
      fontSize: "0.875rem",
      letterSpacing: "0.01em",
      color: active
        ? theme.palette.primary.contrastText
        : theme.palette.text.primary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    ...(active
      ? {
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: "50%",
            transform: "translateY(-50%)",
            width: 4,
            height: "60%",
            borderRadius: "0 4px 4px 0",
            backgroundColor: theme.palette.primary.contrastText,
          },
        }
      : {
          backgroundColor: "transparent",
          "&:hover": {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            "& .MuiListItemIcon-root": {
              color: theme.palette.primary.main,
            },
            "& .MuiListItemText-primary": {
              color: theme.palette.primary.main,
            },
          },
        }),
  });

  return (
    <Box
      sx={{
        width,
        flexShrink: 0,
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${theme.palette.background.paper} 100%)`
            : `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.grey[50], 0.5)} 100%)`,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "width 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        boxShadow:
          theme.palette.mode === "light"
            ? `1px 0 8px ${alpha(theme.palette.grey[400], 0.1)}`
            : "none",
        animation: `${slideInLeft} 0.5s ease-out`,
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          px: collapsed ? 1 : 2,
          py: 2,
          minHeight: 64,
          borderBottom: `.5px solid ${alpha(theme.palette.divider, 0.5)}`,
        }}
      >
        {!collapsed && (
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              fontSize: "0.75rem",
              textTransform: "capitalize",
              letterSpacing: "0.1em",
              color: alpha(theme.palette.text.secondary, 0.8),
            }}
          >
            Navigation
          </Typography>
        )}
        <Tooltip
          title={collapsed ? "Expand menu" : "Collapse menu"}
          open={tipOpen}
          placement="right"
          arrow
          onOpen={() => {
            setTipOpen(true);
            if (tipTimer.current) window.clearTimeout(tipTimer.current);
            tipTimer.current = window.setTimeout(() => setTipOpen(false), 1500);
          }}
          onClose={() => setTipOpen(false)}
          slotProps={{
            tooltip: {
              sx: {
                backgroundColor: theme.palette.grey[800],
                fontSize: "0.75rem",
                fontWeight: 500,
                px: 1.5,
                py: 0.75,
                borderRadius: 1.5,
              },
            },
            arrow: {
              sx: { color: theme.palette.grey[800] },
            },
          }}
        >
          <IconButton
            size="small"
            onClick={() => {
              clearAutoHideTimer();
              setCollapsed((prev) => !prev);
              setTipOpen(true);
              if (tipTimer.current) window.clearTimeout(tipTimer.current);
              tipTimer.current = window.setTimeout(
                () => setTipOpen(false),
                1500,
              );
            }}
            onMouseEnter={() => {
              setTipOpen(true);
              if (tipTimer.current) window.clearTimeout(tipTimer.current);
              tipTimer.current = window.setTimeout(
                () => setTipOpen(false),
                1500,
              );
              if (!collapsed) scheduleAutoHide();
            }}
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              backgroundColor:
                theme.palette.mode === "light"
                  ? alpha(theme.palette.primary.main, 0.08)
                  : alpha(theme.palette.action.hover, 0.4),
              color:
                theme.palette.mode === "light"
                  ? theme.palette.primary.main
                  : theme.palette.text.secondary,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.15),
                color: theme.palette.primary.main,
                transform: "scale(1.05)",
              },
            }}
          >
            {collapsed ? (
              <MenuIcon sx={{ fontSize: "1.25rem" }} />
            ) : (
              <MenuOpenIcon sx={{ fontSize: "1.25rem" }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Menu Items */}
      <Box
        sx={{
          px: collapsed ? 1 : 1.5,
          py: 2,
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          "&::-webkit-scrollbar": {
            width: 4,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: alpha(theme.palette.text.secondary, 0.2),
            borderRadius: 2,
          },
        }}
      >
        <List disablePadding>
          {menuItems.map((item, index) => {
            const active =
              location.pathname.toLowerCase() === item.path.toLowerCase();
            return (
              <Box
                key={`wrapper-${item.key}`}
                sx={{
                  animation: `${fadeInUp} 0.4s ease-out ${0.3 + index * 0.1}s both`,
                }}
              >
                <Tooltip
                  title={collapsed ? item.label : ""}
                  placement="right"
                  arrow
                  disableHoverListener={!collapsed}
                  slotProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: theme.palette.grey[800],
                        color: theme.palette.common.white,
                        fontSize: "0.8rem",
                        fontWeight: 500,
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 1.5,
                        boxShadow: theme.shadows[8],
                        ml: 0.5,
                      },
                    },
                    arrow: {
                      sx: { color: theme.palette.grey[800] },
                    },
                  }}
                >
                  <ListItemButton
                    selected={active}
                    sx={getItemStyles(active)}
                    onClick={() => {
                      navigate(item.path);
                      if (!collapsed) scheduleAutoHide();
                    }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    {!collapsed && <ListItemText primary={item.label} />}
                  </ListItemButton>
                </Tooltip>
              </Box>
            );
          })}
        </List>
      </Box>

      {/* Version Badge */}
      <Box
        sx={{
          mt: "auto",
          px: collapsed ? 1 : 2,
          py: 2,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            py: 1,
            px: 1.5,
            borderRadius: 2,
            backgroundColor: alpha(
              theme.palette.mode === "dark"
                ? theme.palette.success.dark
                : theme.palette.success.light,
              0.15,
            ),
            transition: "all 0.2s ease",
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: theme.palette.success.main,
              "&::after": {
                content: '""',
                position: "absolute",
                top: -2,
                left: -2,
                right: -2,
                bottom: -2,
                borderRadius: "50%",
                border: `2px solid ${alpha(theme.palette.success.main, 0.3)}`,
                animation: "ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite",
              },
              "@keyframes ping": {
                "0%": { transform: "scale(1)", opacity: 1 },
                "75%, 100%": { transform: "scale(1.5)", opacity: 0 },
              },
            }}
          />
          {!collapsed && (
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 600,
                color: theme.palette.success.main,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
              }}
            >
              v{packageJson.version}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Sidebar;
